import { auth } from '../firebase';
import { trackOrderCompleted } from './analyticsService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

class PaymentService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth headers for API requests
  async getAuthHeaders() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const token = await user.getIdToken();
    console.log('[PaymentService] Auth token length:', token ? token.length : 0);
    console.log('[PaymentService] Auth token preview:', token ? token.substring(0, 20) + '...' : 'null');
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  // Create a payment intent
  async createPaymentIntent(orderData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch('https://us-central1-marketplace-5fd46.cloudfunctions.net/createPaymentIntent', {
        method: 'POST',
        headers,
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        // Check if response is JSON or HTML
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create payment intent');
        } else {
          // Handle HTML error responses
          const errorText = await response.text();
          console.error('Server returned HTML error:', errorText);
          throw new Error('Server error: Payment service is temporarily unavailable');
        }
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  // Confirm payment
  async confirmPayment(paymentIntentId, paymentMethodId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}/payments/confirm-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          paymentIntentId,
          paymentMethodId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment confirmation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error confirming payment:', error);
      throw error;
    }
  }

  // Get payment status
  async getPaymentStatus(paymentIntentId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${this.baseURL}/payments/status/${paymentIntentId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get payment status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting payment status:', error);
      throw error;
    }
  }

  // Create order after successful payment
  async createOrder(orderData) {
    try {
      const headers = await this.getAuthHeaders();
      
      // Use the new multi-vendor order system
      const { createParentOrder, createMerchantOrders } = await import('../utils/multiVendorOrderApi');
      
      // Prepare order data for multi-vendor system
      // orderData.total comes from Stripe in cents, convert to dollars
      const totalAmountInCents = parseFloat(orderData.total) || 0;
      const totalAmount = totalAmountInCents / 100; // Convert cents to dollars
      const subtotalAmount = totalAmount / 1.08; // Remove tax to get subtotal
      const taxAmount = totalAmount - subtotalAmount;
      
      console.log('[PaymentService] Amount conversion debug:', {
        totalAmountInCents: totalAmountInCents,
        totalAmount: totalAmount,
        subtotalAmount: subtotalAmount,
        taxAmount: taxAmount
      });
      
      const parentOrderData = {
        items: orderData.items,
        subtotal: parseFloat(subtotalAmount.toFixed(2)), // Save as decimal dollars
        fees: 0, // Calculate fees if needed
        tax: parseFloat(taxAmount.toFixed(2)), // Tax as decimal dollars
        total: parseFloat(totalAmount.toFixed(2)), // Total as decimal dollars
        shipping: 0, // Calculate shipping if needed
        serviceFee: orderData.serviceFee || 2.0, // Vesta's $2 service fee
        deliveryFee: orderData.deliveryFee || 0, // Delivery fee (if any)
        paymentIntentId: orderData.paymentIntentId,
        billingDetails: orderData.billingDetails,
        shippingAddress: orderData.address ? {
          name: orderData.billingDetails?.name || '',
          street: orderData.address,
          city: orderData.billingDetails?.address?.city || '',
          zip: orderData.billingDetails?.address?.postal_code || '',
          phone: orderData.billingDetails?.phone || ''
        } : null
      };

      // Note: Inventory is validated BEFORE payment confirmation in usePayment.js
      // This validation is a double-check in case of race conditions
      // If this fails, payment was already charged, so we log but proceed with order creation
      try {
        console.log('[PaymentService] Double-checking inventory availability (pre-payment validation already done)...');
        const { validateInventoryAvailability } = await import('../utils/inventoryApi');
        
        const validation = await validateInventoryAvailability(orderData.items);
        if (!validation.valid) {
          console.error('[PaymentService] ⚠️ WARNING: Inventory validation failed AFTER payment was charged:', validation.errors);
          console.error('[PaymentService] This indicates a race condition - another order may have consumed inventory between validation and payment');
          // Don't throw - payment already charged, order will be created anyway
          // Seller will need to manually adjust inventory or cancel/refund order
        } else {
          console.log('[PaymentService] Inventory double-check passed');
        }
      } catch (validationError) {
        console.error('[PaymentService] Inventory validation error (non-fatal since payment already charged):', validationError);
        // Don't throw - payment already charged
      }

      // Create parent order
      const parentOrder = await createParentOrder(parentOrderData);
      console.log('[PaymentService] Parent order created:', parentOrder);
      
      // Create merchant orders and order items
      const { merchantOrders, orderItems } = await createMerchantOrders(
        parentOrder.id,
        orderData.items
      );
      console.log('[PaymentService] Merchant orders and items created:', { merchantOrders, orderItems });

      // Update order breakdown with actual Stripe fees (PRD requirement)
      try {
        console.log('[PaymentService] Updating order breakdown with Stripe fees...');
        const { updateOrderBreakdownWithStripeFees } = await import('../utils/multiVendorOrderApi');
        await updateOrderBreakdownWithStripeFees(parentOrder.id, orderData.paymentIntentId);
        console.log('[PaymentService] Order breakdown updated with Stripe fees');
      } catch (breakdownError) {
        console.error('[PaymentService] Failed to update order breakdown:', breakdownError);
        // Don't fail the order if breakdown update fails, but log the error
      }

      // Update inventory for ordered products
      try {
        console.log('[PaymentService] Updating inventory for multi-vendor order...');
        const { updateInventoryAfterOrder } = await import('../utils/inventoryApi');
        
        // Transform order items to the format expected by inventory update
        console.log('[PaymentService] Raw orderItems before transformation:', orderItems);
        console.log('[PaymentService] Order items with serviceDate:', orderItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          serviceDate: item.serviceDate,
          serviceDateType: typeof item.serviceDate,
          serviceDateValue: item.serviceDate
        })));
        
        const inventoryItems = orderItems.map(item => {
          // Get the service date from the order item
          // serviceDate should be set correctly from createMerchantOrders
          const serviceDate = item.serviceDate;
          
          console.log('[PaymentService] Inventory item date mapping:', {
            productId: item.productId,
            serviceDate: serviceDate,
            serviceDateType: typeof serviceDate,
            hasServiceDate: !!serviceDate
          });
          
          return {
            id: item.productId,
            qty: item.quantity,
            fulfillmentDay: serviceDate, // Map serviceDate to fulfillmentDay
            pickupDate: serviceDate, // Also include pickupDate for compatibility
            serviceDate: serviceDate, // Include serviceDate as well
            name: item.productSnapshot?.name || 'Unknown Product',
            productId: item.productId // Ensure productId is available
          };
        });
        
        console.log('[PaymentService] Calling updateInventoryAfterOrder with items:', inventoryItems);
        console.log('[PaymentService] First inventory item details:', {
          ...inventoryItems[0],
          fulfillmentDayType: typeof inventoryItems[0]?.fulfillmentDay,
          fulfillmentDayValue: inventoryItems[0]?.fulfillmentDay
        });
        
        await updateInventoryAfterOrder(inventoryItems, null);
        console.log('[PaymentService] ✅ Inventory updated successfully for order:', parentOrder.id);
      } catch (inventoryError) {
        console.error('[PaymentService] ❌ CRITICAL: Failed to update inventory:', inventoryError);
        console.error('[PaymentService] Inventory error details:', {
          message: inventoryError.message,
          stack: inventoryError.stack,
          orderId: parentOrder.id,
          orderItems: orderItems.map(item => ({
            productId: item.productId,
            serviceDate: item.serviceDate,
            quantity: item.quantity
          }))
        });
        // Don't fail the order if inventory update fails, but log the error
        // TODO: Consider sending an alert to admin for manual inventory adjustment
      }

      // Track order completion for analytics
      try {
        if (orderData.items && orderData.items.length > 0) {
          const productIds = orderData.items.map(item => item.id);
          const firstItem = orderData.items[0];
          if (firstItem.sellerId) {
            const sessionId = sessionStorage.getItem('vesta_session_id') || 'unknown';
            trackOrderCompleted(productIds, firstItem.sellerId, parentOrder.id, auth.currentUser?.uid);
          }
        }
      } catch (trackingError) {
        console.error('Error tracking order completion:', trackingError);
        // Don't fail the order creation if tracking fails
      }

      // Return the parent order with merchant orders for compatibility
      const result = {
        id: parentOrder.id,
        ...parentOrder,
        merchantOrders,
        orderItems
      };
      console.log('[PaymentService] Returning order result:', result);
      return result;
    } catch (error) {
      console.error('Error creating multi-vendor order:', error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService();
